import React from 'react';
import { Button } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { observable, action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Api } from 'services/Api';
import debounce from 'services/debounce';

// Pick a supplier: a search field until one is picked, then its name (click
// to pick another). The menu matches the price book picker's
// (ProductServiceAutocomplete; styles .ps-menu): what it found, then each
// supplier with how to reach them and where they are.

const nameOf = (supplier) => (supplier && ((supplier.attributes && supplier.attributes.name) || supplier.name)) || '';

export const SupplierRow = ({ supplier, active, handleClick }) => {
  const a = supplier.attributes || supplier;
  return (
    <li
      key={supplier.id}
      role="option"
      aria-selected={active}
      className={`ps-row${active ? ' is-active' : ''}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={handleClick}
    >
      <div className="ps-row__main">
        <span className="ps-row__name">{a.name}</span>
        {(a.phone || a.email) && (
          <span className="ps-row__facts">
            {a.phone && <span>{a.phone}</span>}
            {a.email && <span>{a.email}</span>}
          </span>
        )}
        {a.address && <span className="ps-row__description">{a.address}</span>}
      </div>
    </li>
  );
};

@observer
class SupplierAutocomplete extends React.Component {
  @observable loading = false;
  @observable failed = false;
  @observable results = [];
  @observable query = '';
  @observable selectedSupplier = null;
  request = 0;

  componentDidMount() {
    const { selectedSupplierId, value } = this.props;
    if (selectedSupplierId) {
      Api.post('/suppliers/search', { search: { term: '*', where: { id: selectedSupplierId }, per_page: 50, page: 1, order: { name: 'asc' } } })
        .then(action((response) => { this.selectedSupplier = response.data.data[0] || null; }))
        .catch(() => {});
    }
    if (value) this.selectedSupplier = value;
  }

  componentDidUpdate() {
    if (this.props.value) this.selectedSupplier = this.props.value;
  }

  search = debounce((query) => {
    const id = ++this.request;
    Api.post('/suppliers/search', { search: { term: query.trim() || '*', per_page: 20, page: 1, order: { name: 'asc' } } })
      .then(action((response) => {
        if (id !== this.request) return;
        this.loading = false;
        this.failed = false;
        this.results = response.data.data || [];
      }))
      .catch(action(() => {
        if (id !== this.request) return;
        this.loading = false;
        this.failed = true;
      }));
  }, 250);

  @action
  onQueryChange = (query) => {
    this.query = query;
    this.loading = true;
    this.failed = false;
    this.search(query);
  };

  @action
  onSupplierSelect = (supplier) => {
    this.selectedSupplier = supplier;
    this.props.onSupplierSelect(supplier);
  };

  @action
  clear = () => {
    this.selectedSupplier = null;
    this.props.onSupplierSelect(null);
  };

  renderMenu = ({ items, itemsParentRef, renderItem }) => {
    const typed = this.query.trim();
    const rows = items.map(renderItem).filter(Boolean);
    let status;
    if (this.loading) status = typed ? `Searching for “${typed}”…` : 'Loading suppliers…';
    else if (this.failed) status = "Couldn't load suppliers. Check your connection and type again.";
    else if (!typed) status = rows.length ? 'Pick a supplier, or type to search' : 'No suppliers yet. Add them under Suppliers.';
    else if (rows.length) status = `${rows.length} ${rows.length === 1 ? 'supplier matches' : 'suppliers match'} “${typed}”`;
    else status = `No supplier matches “${typed}”. Check the spelling or add them under Suppliers.`;
    return (
      <div className="ps-menu ps-menu--narrow">
        <div className="ps-menu__status" role="status">{status}</div>
        {rows.length > 0 && !this.loading && (
          <ul className="ps-menu__list" ref={itemsParentRef} role="listbox" aria-label="Suppliers">
            {rows}
          </ul>
        )}
      </div>
    );
  };

  render() {
    const { inputProps } = this.props;
    const selected = this.selectedSupplier;
    if (selected) {
      return (
        <div>
          <Button
            text={nameOf(selected)}
            rightIcon="cross"
            className="bp3-text-overflow"
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}
            title={`${nameOf(selected)}: click to pick another`}
            onClick={this.clear}
          />
        </div>
      );
    }
    return (
      <div>
        <Suggest
          inputProps={{ placeholder: 'Choose a supplier', ...inputProps }}
          closeOnSelect
          openOnKeyDown={false}
          resetOnQuery
          resetOnSelect
          query={this.query}
          popoverProps={{ minimal: true, usePortal: true, popoverClassName: 'ps-menu-popover', onOpening: () => { if (!this.results.length && !this.loading) this.onQueryChange(this.query); } }}
          onQueryChange={this.onQueryChange}
          items={toJS(this.results)}
          itemRenderer={(supplier, { handleClick, modifiers }) => (
            <SupplierRow key={supplier.id} supplier={supplier} active={modifiers.active} handleClick={handleClick} />
          )}
          itemListRenderer={this.renderMenu}
          inputValueRenderer={(supplier) => nameOf(supplier)}
          onItemSelect={this.onSupplierSelect}
          noResults={null}
        />
      </div>
    );
  }
}

SupplierAutocomplete.propTypes = {
  inputProps: PropTypes.object,
  onSupplierSelect: PropTypes.func.isRequired,
  selectedSupplierId: PropTypes.string,
};

export default SupplierAutocomplete;
